import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Chip, Alert, alpha,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
  Edit as EditIcon,
  Event as EventIcon,
  EventAvailable as EventSidebarIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Computer as ComputerIcon,
  Engineering as EngineeringIcon,
  Settings as SettingsIcon,
  DateRange as DateRangeIcon,
  AccessTime as TimeIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon,
  Flag as StatusFlagIcon,
  Circle as CircleIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { eventService } from '../../../services/eventService';
import { useAuth } from '../../../contexts/AuthContext';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { canModifyEvent, canCancelEvent } from '../../../utils/eventPenaltyPermissions';
import { cancelViaBulkUpdate } from '../../../utils/resourceCancel';
import { formatMalaysiaTime } from '../../../utils/malaysiaTime';
import { EVENT_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import axiosInstance from '../../../utils/axios';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const NEON        = '#38BDF8';
const NEON2       = '#818CF8';
const NEON3       = '#34D399';
const AMBER       = '#FBBF24';
const CORAL       = '#F87171';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';

const STATUS_MAP = {
  upcoming:  { color: NEON,  label: 'Upcoming' },
  ongoing:   { color: NEON3, label: 'Ongoing' },
  completed: { color: NEON2, label: 'Completed' },
  cancelled: { color: CORAL, label: 'Cancelled' },
  postponed: { color: AMBER, label: 'Postponed' },
  pending:   { color: AMBER, label: 'Pending' },
};
const getStatus = (s) => {
  const key = s?.toLowerCase();
  if (STATUS_MAP[key]) return STATUS_MAP[key];
  if (!s) return { color: NEON2, label: 'Unknown' };
  return { color: NEON2, label: String(s).charAt(0).toUpperCase() + String(s).slice(1) };
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
function parseSelectionIds(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(Number).filter((n) => !Number.isNaN(n));
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p.map(Number).filter((n) => !Number.isNaN(n)) : []; }
    catch { return []; }
  }
  return [];
}
const nameById = (list, id) => list.find((x) => String(x.id) === String(id))?.name || `#${id}`;

function pdfSafeText(v) {
  if (v == null) return '';
  return String(v)
    .replace(/→/g, '->').replace(/←/g, '<-')
    .replace(/[–—‐‒―−]/g, '-')
    .replace(/[""]/g, '"').replace(/['']/g, "'")
    .replace(/…/g, '...').replace(/\u00a0/g, ' ')
    .replace(/\r\n|\n|\r/g, ' ')
    .replace(/[^\x20-\x7E]/g, '');
}
function formatTimeForPdf(t) {
  if (!t) return '';
  return formatMalaysiaTime(t) ?? pdfSafeText(String(t));
}
function formatTimeShort(value) {
  return formatMalaysiaTime(value);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Primitives
// ═══════════════════════════════════════════════════════════════════════════════

const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children, action }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '7px',
          background: alpha(accent, 0.12), border: `1px solid ${alpha(accent, 0.22)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 13, color: accent }} />
        </Box>
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
          fontSize: '0.78rem', letterSpacing: '0.07em', textTransform: 'uppercase',
          color: alpha(TEXT_BODY, 0.85),
        }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const ProseBlock = ({ value, accent = NEON, emptyLabel = 'Nothing added yet.' }) => (
  <Box sx={{
    px: 2, py: 1.8, borderRadius: '8px',
    background: alpha(accent, 0.04),
    border: `1px solid ${alpha(accent, 0.1)}`,
    borderLeft: `3px solid ${alpha(accent, value ? 0.5 : 0.2)}`,
  }}>
    <Typography sx={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '0.9rem', lineHeight: 1.75,
      color: value ? alpha(TEXT_BODY, 0.9) : TEXT_MUTED,
      fontStyle: value ? 'normal' : 'italic',
      whiteSpace: 'pre-wrap',
    }}>
      {value || emptyLabel}
    </Typography>
  </Box>
);

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub }) => (
  <Box sx={{
    position: 'relative', borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    transition: 'box-shadow 0.25s, border-color 0.25s',
    '&:hover': { boxShadow: `0 0 36px ${alpha(color, 0.16)}, inset 0 1px 0 ${alpha(color, 0.18)}`, borderColor: alpha(color, 0.35) },
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'JetBrains Mono', monospace", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

const AudiencePill = ({ label, color = NEON }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.6,
    px: 1.4, py: 0.5, borderRadius: '6px',
    background: alpha(color, 0.09), border: `1px solid ${alpha(color, 0.25)}`,
  }}>
    <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: alpha(TEXT_BODY, 0.9) }}>
      {label}
    </Typography>
  </Box>
);

const PhaseRow = ({ label, start, end, color = NEON, icon: Icon, isLast }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '160px 1fr auto',
    alignItems: 'center', gap: 2, py: 1.2, px: 1.5,
    borderRadius: '8px', mb: isLast ? 0 : 0.5,
    transition: 'background 0.18s',
    '&:hover': { background: alpha(color, 0.05) },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && <Icon sx={{ fontSize: 12, color: alpha(color, 0.65) }} />}
      <Typography sx={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.62rem', fontWeight: 600,
        letterSpacing: '0.1em', textTransform: 'uppercase', color: alpha(color, 0.75),
      }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ height: '2px', flex: 1, maxWidth: 120, borderRadius: '2px', background: `linear-gradient(90deg, ${alpha(color, 0.55)}, ${alpha(color, 0.15)})` }} />
    </Box>
    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', fontWeight: 500, color: TEXT_BODY, whiteSpace: 'nowrap' }}>
      {start} <span style={{ color: alpha(color, 0.5), margin: '0 4px' }}>→</span> {end}
    </Typography>
  </Box>
);

const TH = ({ children, accent = NEON }) => (
  <TableCell sx={{
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.6rem', fontWeight: 600,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    color: alpha(accent, 0.65), background: alpha(accent, 0.04),
    borderBottom: `1px solid ${alpha(accent, 0.14)}`, py: 1.5, px: 2,
  }}>{children}</TableCell>
);
const TD = ({ children }) => (
  <TableCell sx={{
    fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem', color: TEXT_BODY,
    borderBottom: `1px solid ${alpha(NEON, 0.06)}`, py: 1.4, px: 2,
  }}>{children}</TableCell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const EventDetails = () => {
  const [event, setEvent]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [users, setUsers]           = useState([]);
  const [departments, setDepartments] = useState([]);
  const [bureaus, setBureaus]       = useState([]);
  const [managements, setManagements] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { id }                = useParams();
  const navigate              = useNavigate();
  const { user: currentUser } = useAuth();

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchInitialData();
  }, [id, currentUser]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const eventData = await eventService.getById(Number(id));
      setEvent(eventData);
      void (async () => {
        try {
          const [usersRes, deptRes, bureauRes, mgmtRes] = await Promise.all([
            axiosInstance.get('/users?minimal=1'),
            axiosInstance.get('/departments?minimal=1'),
            axiosInstance.get('/bureaus'),
            axiosInstance.get('/managements'),
          ]);
          setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || []);
          const norm = (r) => (Array.isArray(r?.data) ? r.data : r?.data?.data || []);
          setDepartments(norm(deptRes));
          setBureaus(norm(bureauRes));
          setManagements(norm(mgmtRes));
        } catch {}
      })();
    } catch {
      setError('Failed to load event details.');
    } finally {
      setLoading(false);
    }
  };

  const userNameMap = useMemo(
    () => new Map((users || []).map((u) => [String(u.id), u.name])),
    [users]
  );
  const getUserName = (uid) => userNameMap.get(String(uid)) || 'Unknown';

  // ─── PDF ──────────────────────────────────────────────────────────────────
  const generateReport = () => {
    if (!event) return;
    const doc   = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 40;
    const baseTable = {
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 5, overflow: 'linebreak', textColor: [30, 30, 30] },
      headStyles: { fillColor: [20, 55, 90], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      theme: 'striped',
      alternateRowStyles: { fillColor: [248, 250, 252] },
    };
    const sectionTitle = (title, yPos) => {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(20, 55, 90);
      doc.text(pdfSafeText(title), margin, yPos);
      doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      return yPos + 18;
    };
    const fmtD = (d) => (d && dayjs(d).isValid() ? pdfSafeText(dayjs(d).format('MMM DD, YYYY')) : '-');
    const td = event.training_details;
    let y = 36;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20, 55, 90);
    doc.text('Event report', pageW / 2, y, { align: 'center' });
    y += 28; doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
    y = sectionTitle('General', y);
    const eventTimePdf = event.start_time ? `${formatTimeForPdf(event.start_time)} - ${formatTimeForPdf(event.end_time)}` : 'Not specified';
    autoTable(doc, { ...baseTable, startY: y, head: [['Field', 'Value']], body: [
      ['Name', pdfSafeText(event.name)], ['Type', pdfSafeText(event.type)],
      ['Status', pdfSafeText(event.status ?? '-')], ['Venue', pdfSafeText(event.venue ?? '-')],
      ['Period (start / end)', `${fmtD(event.start_date)} to ${fmtD(event.end_date)}`],
      ['Time', pdfSafeText(eventTimePdf)],
      ['Remarks', pdfSafeText(event.remark?.trim() || '-')],
    ]});
    y = doc.lastAutoTable.finalY + 22;
    y = sectionTitle('Audience and visibility', y);
    const dIds2 = parseSelectionIds(event.department_selection);
    const bIds2 = parseSelectionIds(event.bureau_selection);
    const mIds2 = parseSelectionIds(event.management_selection);
    const audienceRows = event.all_users
      ? [['Scope', 'All team members (no filter)']]
      : [
          ['Departments', dIds2.length ? dIds2.map((i) => nameById(departments, i)).join(', ') : '-'],
          ['Bureaus',     bIds2.length ? bIds2.map((i) => nameById(bureaus, i)).join(', ')     : '-'],
          ['Managements', mIds2.length ? mIds2.map((i) => nameById(managements, i)).join(', ') : '-'],
        ];
    autoTable(doc, { ...baseTable, startY: y, head: [['Field', 'Value']], body: audienceRows });
    y = doc.lastAutoTable.finalY + 22;
    if (['Demo', 'Prototype'].includes(event.type) && td) {
      y = sectionTitle('Development timeline', y);
      const timelineBody = [];
      if (td.overall_start_date || td.overall_end_date) timelineBody.push(['Overall', fmtD(td.overall_start_date), fmtD(td.overall_end_date)]);
      timelineBody.push(
        ['Design', fmtD(td.design_start_date), fmtD(td.design_end_date)],
        ['Mechanism', fmtD(td.mechanism_start_date), fmtD(td.mechanism_end_date)],
        ['Electronic', fmtD(td.electronic_start_date), fmtD(td.electronic_end_date)],
        ['Programming', fmtD(td.programming_start_date), fmtD(td.programming_end_date)],
        ['Tuning', fmtD(td.tuning_start_date), fmtD(td.tuning_end_date)],
      );
      autoTable(doc, { ...baseTable, startY: y, head: [['Phase', 'Start', 'End']], body: timelineBody });
      y = doc.lastAutoTable.finalY + 18;
      y = sectionTitle('Demo day', y);
      autoTable(doc, { ...baseTable, startY: y, head: [['Date', 'Time']], body: [[fmtD(td.demo_day), td.demo_time ? formatTimeForPdf(td.demo_time) : '-']] });
      y = doc.lastAutoTable.finalY + 22;
      let teams = td.teams;
      if (typeof teams === 'string') { try { teams = JSON.parse(teams); } catch { teams = []; } }
      if (teams?.length) {
        y = sectionTitle('Teams', y);
        const teamRows = [];
        teams.forEach((t, i) => {
          const teamLabel = pdfSafeText(t.team_name || `Team ${i + 1}`);
          const groups = [['Programming', t.programming_members || []], ['Electronic', t.electronic_members || []], ['Mechanism', t.mechanism_members || []]];
          groups.forEach(([groupLabel, members]) => {
            if (!members.length) { teamRows.push([teamLabel, groupLabel, '-']); return; }
            members.forEach((uid, idx) => { teamRows.push([idx === 0 ? teamLabel : '', idx === 0 ? groupLabel : '', pdfSafeText(getUserName(uid))]); });
          });
        });
        autoTable(doc, { ...baseTable, startY: y, head: [['Team', 'Group', 'Member']], body: teamRows });
        y = doc.lastAutoTable.finalY + 22;
      }
    }
    if (event.type === 'Training' && td?.teams) {
      let tteams = td.teams;
      if (typeof tteams === 'string') { try { tteams = JSON.parse(tteams); } catch { tteams = []; } }
      if (tteams?.length) {
        y = sectionTitle('Training teams', y);
        const trainingRows = [];
        tteams.forEach((t, i) => {
          const teamLabel = pdfSafeText(t.team_name || `Team ${i + 1}`);
          let teamShown = false;
          [1, 2, 3].forEach((n) => { const uid = t[`operator_${n}_id`]; if (uid) { trainingRows.push([teamShown ? '' : teamLabel, `Operator ${n}`, pdfSafeText(getUserName(uid))]); teamShown = true; } });
          [1, 2, 3].forEach((n) => { const uid = t[`pit_crew_${n}_id`]; if (uid) { trainingRows.push([teamShown ? '' : teamLabel, `Pit Crew ${n}`, pdfSafeText(getUserName(uid))]); teamShown = true; } });
        });
        autoTable(doc, { ...baseTable, startY: y, head: [['Team', 'Role', 'Member']], body: trainingRows.length ? trainingRows : [['-', '-', '-']] });
        y = doc.lastAutoTable.finalY + 22;
      }
    }
    const lc = event.lab_cleaning_details;
    if (event.type === 'LabCleaning' && lc) {
      y = sectionTitle('Lab cleaning representatives', y);
      const keys = [['Programming Room','programming_representatives'],['Electronic Room','electronic_representatives'],['Mechanism Room','mechanism_representatives'],['Inventory','inventory_representatives'],['Meeting','meeting_representatives'],['Mop Floor','mop_floor_representatives']];
      const repRows = [];
      keys.forEach(([lbl, k]) => {
        let reps = lc[k];
        if (typeof reps === 'string') { try { reps = JSON.parse(reps); } catch { reps = []; } }
        if (!Array.isArray(reps)) reps = [];
        if (!reps.length) { repRows.push([pdfSafeText(lbl), '-']); return; }
        reps.forEach((uid, idx) => { repRows.push([idx === 0 ? pdfSafeText(lbl) : '', pdfSafeText(getUserName(uid))]); });
      });
      autoTable(doc, { ...baseTable, startY: y, head: [['Area', 'Representative']], body: repRows });
      y = doc.lastAutoTable.finalY + 22;
    }
    y = sectionTitle('Record', y);
    autoTable(doc, { ...baseTable, startY: y, head: [['Field', 'Value']], body: [
      ['Created',      event.created_at ? dayjs(event.created_at).format('MMM DD, YYYY HH:mm') : '-'],
      ['Created by',   event.created_by ? getUserName(event.created_by) : '-'],
      ['Last updated', event.updated_at ? dayjs(event.updated_at).format('MMM DD, YYYY HH:mm') : '-'],
      ['Updated by',   event.updated_by ? getUserName(event.updated_by) : '-'],
    ]});
    doc.save(`event-report-${event.id}.pdf`);
  };

  const handleCancelEvent = async () => {
    try {
      const eventName = event?.name || 'Event';
      await cancelViaBulkUpdate(EVENT_BULK_CONFIG.endpoint, id, 'Cancelled');
      setEvent((prev) => (prev ? { ...prev, status: 'Cancelled' } : prev));
      setCancelDialogOpen(false);
      setSuccessMessage(`Event "${eventName}" has been cancelled.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to cancel event. Please try again.');
      setCancelDialogOpen(false);
    }
  };

  if (!currentUser) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Alert severity="error">Please log in.</Alert>
    </Container>
  );
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading event details" size={56} variant="primary" />
    </Box>
  );
  if (!event) return null;

  // ─── Derived ───────────────────────────────────────────────────────────────
  const statusCfg  = getStatus(event.status);
  const isTypeDev  = ['Demo', 'Prototype'].includes(event.type);
  const isTraining = event.type === 'Training';
  const isLabClean = event.type === 'LabCleaning';
  const startTimeShort = formatTimeShort(event.start_time);
  const endTimeShort   = formatTimeShort(event.end_time);
  const timeStr = startTimeShort && endTimeShort
    ? `${startTimeShort} – ${endTimeShort}`
    : startTimeShort || endTimeShort || null;
  const dIds = parseSelectionIds(event.department_selection);
  const bIds = parseSelectionIds(event.bureau_selection);
  const mIds = parseSelectionIds(event.management_selection);

  const fmt = (v) => (v && dayjs(v).isValid() ? dayjs(v).format('ddd, D MMM YYYY') : null);
  const fmtShort = (v) => (v && dayjs(v).isValid() ? dayjs(v).format('D MMM YYYY') : null);
  const periodStart = fmtShort(event.start_date);
  const periodEnd = fmtShort(event.end_date);
  const periodLabel = periodStart && periodEnd
    ? (periodStart === periodEnd ? periodStart : `${periodStart} – ${periodEnd}`)
    : periodStart || periodEnd || '—';
  const periodSub = timeStr ? `Daily ${timeStr}` : undefined;
  const audienceSummary = event.all_users
    ? 'All team members'
    : [
        dIds.length && `${dIds.length} Dept`,
        bIds.length && `${bIds.length} Bureau`,
        mIds.length && `${mIds.length} Mgmt`,
      ].filter(Boolean).join(' · ') || 'Filtered audience';

  // ─── Section renderers ─────────────────────────────────────────────────────

  const renderRemarks = () => {
    if (!event.remark?.trim()) return null;
    return (
      <Section title="Remarks" icon={NotesIcon} accent={NEON2} delay={0.1}>
        <ProseBlock value={event.remark.trim()} accent={NEON2} emptyLabel="No remarks added." />
      </Section>
    );
  };

  const renderAudienceSection = () => {
    if (event.all_users || (dIds.length === 0 && bIds.length === 0 && mIds.length === 0)) return null;
    return (
      <Section title="Audience Breakdown" icon={GroupIcon} accent={NEON2} delay={0.12}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
          {dIds.map((i) => <AudiencePill key={`d${i}`} label={nameById(departments, i)} color={NEON} />)}
          {bIds.map((i) => <AudiencePill key={`b${i}`} label={nameById(bureaus, i)} color={NEON2} />)}
          {mIds.map((i) => <AudiencePill key={`m${i}`} label={nameById(managements, i)} color={NEON3} />)}
        </Box>
      </Section>
    );
  };

  const renderDemoPrototype = () => {
    if (!isTypeDev || !event.training_details) return null;
    const d = event.training_details;
    const phases = [
      { label: 'Design',      start: d.design_start_date,      end: d.design_end_date,      color: NEON,  icon: DateRangeIcon   },
      { label: 'Mechanism',   start: d.mechanism_start_date,   end: d.mechanism_end_date,   color: NEON2, icon: SettingsIcon    },
      { label: 'Electronic',  start: d.electronic_start_date,  end: d.electronic_end_date,  color: AMBER, icon: EngineeringIcon },
      { label: 'Programming', start: d.programming_start_date, end: d.programming_end_date, color: NEON3, icon: ComputerIcon    },
      { label: 'Tuning',      start: d.tuning_start_date,      end: d.tuning_end_date,      color: CORAL, icon: SettingsIcon    },
    ];
    return (
      <>
        <Section title="Development Timeline" icon={ScheduleIcon} accent={NEON} delay={0.1}>
          {d.overall_start_date && dayjs(d.overall_start_date).isValid() && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, mb: 1.5, borderRadius: '8px', background: alpha(NEON, 0.07), border: `1px solid ${alpha(NEON, 0.18)}` }}>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha(NEON, 0.7) }}>Overall Span</Typography>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 600, color: NEON }}>
                {fmt(d.overall_start_date)} <span style={{ opacity: 0.5 }}>→</span> {fmt(d.overall_end_date)}
              </Typography>
            </Box>
          )}
          <Box sx={{ borderRadius: '10px', border: `1px solid ${alpha(NEON, 0.08)}`, overflow: 'hidden' }}>
            {phases.map((p, i) => <PhaseRow key={p.label} label={p.label} start={fmt(p.start)} end={fmt(p.end)} color={p.color} icon={p.icon} isLast={i === phases.length - 1} />)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 2, py: 1.5, borderRadius: '10px', background: `linear-gradient(90deg, ${alpha(CORAL, 0.12)}, ${alpha(CORAL, 0.04)})`, border: `1px solid ${alpha(CORAL, 0.3)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon sx={{ fontSize: 15, color: CORAL }} />
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha(CORAL, 0.8) }}>Demo Day</Typography>
            </Box>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.92rem', fontWeight: 700, color: TEXT_BODY }}>
              {fmt(d.demo_day)}
              {d.demo_time && <span style={{ color: CORAL, marginLeft: 6 }}>@ {formatTimeShort(d.demo_time) || '—'}</span>}
            </Typography>
          </Box>
        </Section>
        {renderTeamTable(d.teams, 'demo-prototype')}
      </>
    );
  };

  const renderTeamTable = (teams, mode = 'demo-prototype') => {
    let parsed = teams;
    if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch { return null; } }
    if (!parsed?.length) return null;
    const isDev   = mode === 'demo-prototype';
    const isRoles = mode === 'training';
    return (
      <Section title="Team Assignments" icon={GroupIcon} accent={NEON2} delay={0.12}>
        <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${alpha(NEON2, 0.1)}` }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {isDev   && ['Team', 'Programming', 'Electronic', 'Mechanism'].map((h) => <TH key={h} accent={NEON2}>{h}</TH>)}
                {isRoles && ['Team', 'Operators', 'Pit Crew'].map((h) => <TH key={h} accent={NEON2}>{h}</TH>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {parsed.map((team, idx) => (
                <TableRow key={idx} sx={{ background: idx % 2 === 0 ? 'transparent' : alpha(SURFACE_UP, 0.25), '&:hover': { background: alpha(NEON2, 0.04) }, transition: 'background 0.15s' }}>
                  <TD>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: NEON2, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem', color: TEXT_BODY }}>{team.team_name || `Team ${idx + 1}`}</Typography>
                    </Box>
                  </TD>
                  {isDev && [['programming_members', NEON], ['electronic_members', NEON2], ['mechanism_members', NEON3]].map(([key, n]) => (
                    <TD key={key}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {team[key]?.length ? team[key].map((mid) => (
                          <Chip key={mid} label={getUserName(mid)} size="small" sx={{ height: 20, fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', fontWeight: 500, bgcolor: alpha(n, 0.1), color: n, border: `1px solid ${alpha(n, 0.25)}`, borderRadius: '5px', '& .MuiChip-label': { px: 1 } }} />
                        )) : <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontStyle: 'italic' }}>—</Typography>}
                      </Box>
                    </TD>
                  ))}
                  {isRoles && (
                    <>
                      <TD>{[1,2,3].filter((n) => team[`operator_${n}_id`]).map((n) => (
                        <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3 }}>
                          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.58rem', color: alpha(NEON3, 0.6), minWidth: 24 }}>Op{n}</Typography>
                          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: TEXT_BODY }}>{getUserName(team[`operator_${n}_id`])}</Typography>
                        </Box>
                      ))}</TD>
                      <TD>{[1,2,3].filter((n) => team[`pit_crew_${n}_id`]).map((n) => (
                        <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3 }}>
                          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.58rem', color: alpha(NEON2, 0.6), minWidth: 24 }}>PC{n}</Typography>
                          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: TEXT_BODY }}>{getUserName(team[`pit_crew_${n}_id`])}</Typography>
                        </Box>
                      ))}</TD>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    );
  };

  const renderLabCleaning = () => {
    if (!isLabClean || !event.lab_cleaning_details) return null;
    const d = event.lab_cleaning_details;
    const areas = [
      { key: 'programming_representatives', label: 'Programming Room', color: NEON,  icon: ComputerIcon    },
      { key: 'electronic_representatives',  label: 'Electronic Room',  color: NEON2, icon: EngineeringIcon },
      { key: 'mechanism_representatives',   label: 'Mechanism Room',   color: NEON3, icon: SettingsIcon    },
      { key: 'inventory_representatives',   label: 'Inventory',        color: AMBER, icon: PersonIcon      },
      { key: 'meeting_representatives',     label: 'Meeting',          color: CORAL, icon: GroupIcon       },
      { key: 'mop_floor_representatives',   label: 'Mop Floor',        color: NEON2, icon: PersonIcon      },
    ];
    return (
      <Section title="Lab Cleaning Representatives" icon={SettingsIcon} accent={AMBER} delay={0.08}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {areas.map((area, i) => {
            let reps = d[area.key];
            if (typeof reps === 'string') { try { reps = JSON.parse(reps); } catch { reps = []; } }
            if (!Array.isArray(reps)) reps = [];
            const isLast = i === areas.length - 1;
            return (
              <Box key={area.key} sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2, py: 1.2, px: 1, borderBottom: isLast ? 'none' : `1px solid ${alpha(NEON, 0.06)}`, transition: 'background 0.15s', '&:hover': { background: alpha(area.color, 0.04) } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <area.icon sx={{ fontSize: 12, color: alpha(area.color, 0.65) }} />
                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha(area.color, 0.8) }}>{area.label}</Typography>
                </Box>
                {reps.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                    {reps.map((uid) => (
                      <Box key={uid} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.3, borderRadius: '5px', background: alpha(area.color, 0.08), border: `1px solid ${alpha(area.color, 0.2)}` }}>
                        <PersonIcon sx={{ fontSize: 10, color: alpha(area.color, 0.6) }} />
                        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: TEXT_BODY, fontWeight: 500 }}>{getUserName(uid)}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: TEXT_MUTED, fontStyle: 'italic' }}>No representatives assigned</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Section>
    );
  };

  const renderTraining = () => {
    if (!isTraining || !event.training_details) return null;
    return renderTeamTable(event.training_details.teams, 'training');
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>
      <Header
        title="Event Details"
        subtitle="View event information"
        icon={EventSidebarIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
              onClick={generateReport}
              sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`, color: NEON, transition: 'all 0.2s', '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` } }}
            >Report</Button>
            {canModifyEvent(currentUser, event) && (
              <>
                <Button
                  startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                  onClick={() => navigate(`/events/edit/${id}`)}
                  sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`, color: NEON, transition: 'all 0.2s', '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` } }}
                >Edit</Button>
                {canCancelEvent(currentUser, event) && (
                  <Button
                    startIcon={<CancelIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setCancelDialogOpen(true)}
                    sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`, color: CORAL, transition: 'all 0.2s', '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` } }}
                  >Cancel Event</Button>
                )}
              </>
            )}
          </Box>
        }
      />

      {error && <FeedbackMessage message={error} severity="error" onClose={() => setError(null)} sx={{ mb: 2.5 }} />}
      {successMessage && <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2.5 }} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '13px', flexShrink: 0, background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`, border: `2px solid ${alpha(NEON2, 0.3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EventSidebarIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: "'DM Sans', sans-serif", color: TEXT_BODY, letterSpacing: -0.3, lineHeight: 1.15 }}>
                  {event.name}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  {event.type}{event.venue ? ` · ${event.venue}` : ''}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.4, py: 0.5, borderRadius: '20px', background: alpha(statusCfg.color, 0.1), border: `1px solid ${alpha(statusCfg.color, 0.3)}`, alignSelf: 'flex-start' }}>
              <CircleIcon sx={{ fontSize: 7, color: statusCfg.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: statusCfg.color, letterSpacing: '0.05em' }}>
                {statusCfg.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Event Type" value={event.type} icon={EventIcon} color={NEON} />
            <MetricCard label="Period" value={periodLabel} icon={DateRangeIcon} color={NEON2} sub={periodSub} />
            <MetricCard label="Venue" value={event.venue} icon={LocationIcon} color={NEON3} />
            <MetricCard label="Audience" value={audienceSummary} icon={GroupIcon} color={NEON} />
          </Box>
        </Box>

        {renderRemarks()}
        {renderAudienceSection()}

        {/* ── Type-specific sections ────────────────────────────────────────── */}
        {renderDemoPrototype()}
        {renderLabCleaning()}
        {renderTraining()}

        {/* ── System Information (UserDetails style) ───────────────────────── */}
        <SystemInformationSection
          createdBy={event.created_by ? getUserName(event.created_by) : 'Not available'}
          createdAt={event.created_at ? dayjs(event.created_at).format('MMM D YYYY, HH:mm') : null}
          updatedBy={event.updated_by ? getUserName(event.updated_by) : 'Not available'}
          updatedAt={event.updated_at ? dayjs(event.updated_at).format('MMM D YYYY, HH:mm') : null}
          updatedAtLabel="Last Updated"
          delay={0.18}
          accentColor={NEON2}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmDeleteDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelEvent}
        title="Cancel Event"
        itemType="event"
        itemName={event?.name}
        description="The event will be marked as Cancelled and remain in the system for records."
        confirmLabel="Cancel Event"
      />
    </Container>
  );
};

export default EventDetails;